import { getFirestore } from "firebase/firestore";
import { getAnalytics, isSupported } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";

// Helper para pegar variáveis de ambiente (Vite/Node)
const getEnv = (key: string): string => {
  try {
    // @ts-ignore - Vite environment
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      // @ts-ignore
      return import.meta.env[key];
    }
  } catch (e) {
    // Ignore error
  }

  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key] as string;
    }
  } catch (e) {
    // Ignore error
  }

  return "";
};

// Configuração do Firebase - valores obtidos de variáveis de ambiente da Vercel
const firebaseConfig = {
  apiKey: getEnv("VITE_FIREBASE_API_KEY") || "meu_token",
  authDomain: getEnv("VITE_FIREBASE_AUTH_DOMAIN") || "meu_projeto.firebaseapp.com",
  projectId: getEnv("VITE_FIREBASE_PROJECT_ID") || "meu_projeto",
  storageBucket: getEnv("VITE_FIREBASE_STORAGE_BUCKET") || "meu_projeto.firebasestorage.app",
  messagingSenderId: getEnv("VITE_FIREBASE_MESSAGING_SENDER_ID") || "meu_token",
  appId: getEnv("VITE_FIREBASE_APP_ID") || "meu_token",
  measurementId: getEnv("VITE_FIREBASE_MEASUREMENT_ID") || "meu_token"
};

// Verifica se a API Key está configurada (obrigatória)
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey);

if (!isFirebaseConfigured) {
  console.error("Firebase não está configurado. Configure VITE_FIREBASE_API_KEY nas variáveis de ambiente.");
}

// Initialize Firebase (compat mode para compatibilidade)
let app;
if (!firebase.apps.length) {
  app = firebase.initializeApp(firebaseConfig);
} else {
  app = firebase.app();
}

// ============================================
// FIREBASE APP CHECK (Crítico #2 — Anti-abuso)
// ============================================
// App Check valida que requisições vêm de uma instância legítima do app
// (não de bots que pegaram a VITE_FIREBASE_API_KEY do bundle público).
//
// COMO ATIVAR (passos manuais):
//   1. Firebase Console → App Check → Apps → Register web app → Provider: reCAPTCHA v3
//   2. Copiar a "Site Key" gerada
//   3. Vercel → Environment Variables → adicionar VITE_RECAPTCHA_SITE_KEY=<site-key>
//   4. Deploy do frontend → aguardar 24h coletando métricas no Firebase Console
//   5. Quando métricas mostrarem >95% de requests com token válido:
//      → Firebase Console → App Check → Enforce em Authentication, Firestore, Storage, Functions
//   6. No backend (functions/index.js): mudar APP_CHECK_ENFORCED=true como variável de ambiente
//
// Enquanto VITE_RECAPTCHA_SITE_KEY não estiver setada, App Check é skipped silenciosamente.
const recaptchaSiteKey = getEnv("VITE_RECAPTCHA_SITE_KEY");
if (recaptchaSiteKey && typeof window !== 'undefined') {
  try {
    initializeAppCheck(app as any, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true
    });
  } catch (e) {
    console.error('[App Check] Falha ao inicializar:', e);
  }
}

// Export auth instance
export const auth = app.auth();
export const googleProvider = new firebase.auth.GoogleAuthProvider();

// Export Firestore database
export const db = getFirestore(app as any);

// Export Analytics (initialized lazily to support SSR)
let analyticsInstance: ReturnType<typeof getAnalytics> | null = null;

export const getFirebaseAnalytics = async () => {
  if (analyticsInstance) return analyticsInstance;

  const supported = await isSupported();
  if (supported && isFirebaseConfigured) {
    analyticsInstance = getAnalytics(app as any);
  }
  return analyticsInstance;
};